
import React, { createContext, useContext, useEffect, useState } from 'react';
import { User } from 'firebase/auth';
import { clearIndexedDbPersistence, disableNetwork, enableNetwork } from 'firebase/firestore';
import { auth, db, doc, onSnapshot, getDoc, signOut, httpsCallable, firebaseFunctions, USE_EMULATORS } from '../services/firebaseService';
import { UserProfile } from '../types';
import { derivePermissions, Permissions } from '../utils/permissions';
import { ConnectionStatusScreen } from '../components/ConnectionStatusScreen';
import { turnstileAppCheckProviderInstance } from '../services/TurnstileAppCheckProvider';

// Das eigene Profil darf jede angemeldete Person lesen. Scheitert das Laden, liegt es an der Verbindung,
// meist an App Check (Turnstile) auf einer langsamen Leitung: wiederholen statt "keine Organisation" melden.
// Rund 30 Sekunden "Verbindung wird hergestellt": Turnstile braucht auf schwachen Mobilverbindungen
// nach dem Laden des Skripts oft noch über 8 Sekunden (gemessen über einen Mobile-Hotspot).
const PROFILE_RETRY_DELAYS_MS = [2000, 4000, 8000, 8000];
const PROFILE_BACKGROUND_RETRY_MS = 10000;

interface AuthContextType {
  currentUser: User | null;
  userProfile: UserProfile | null;
  loading: boolean;
  /** Tenant Admin (Struktur, Personen, Einstellungen) */
  isAdmin: boolean;
  isSuperAdmin: boolean;
  isAssessor: boolean;
  permissions: Permissions;
  /** Profil konnte wegen der Verbindung nicht geladen werden (nicht: kein Profil vorhanden) */
  profileLoadFailed: boolean;
  isRetryingProfile: boolean;
  retryProfileLoad: () => void;
  /** Abmelden, ohne dass Firestore danach noch für die abgemeldete Person anfragt */
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  currentUser: null,
  userProfile: null,
  loading: true,
  isAdmin: false,
  isSuperAdmin: false,
  isAssessor: false,
  permissions: derivePermissions(null),
  profileLoadFailed: false,
  isRetryingProfile: false,
  retryProfileLoad: () => {},
  logout: async () => {},
});

// eslint-disable-next-line react-refresh/only-export-components
export const useAuth = () => useContext(AuthContext);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);

  // Track if we already logged login for this session to prevent duplicate logs on refresh
  const loginLogged = React.useRef(false);

  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [isReconnecting, setIsReconnecting] = useState(false);
  const [profileLoadFailed, setProfileLoadFailed] = useState(false);
  const [isRetryingProfile, setIsRetryingProfile] = useState(false);
  const retryProfileRef = React.useRef<() => void>(() => {});
  const stopProfileListenerRef = React.useRef<() => void>(() => {});
  const [isSigningOut, setIsSigningOut] = useState(false);
  const signOutRef = React.useRef<{ done: Promise<void>; finish: () => void } | null>(null);

  useEffect(() => {
    // Keep track of the snapshot listener unsubscriber
    let unsubscribeProfileSnapshot: (() => void) | null = null;
    stopProfileListenerRef.current = () => {
      if (unsubscribeProfileSnapshot) {
        unsubscribeProfileSnapshot();
        unsubscribeProfileSnapshot = null;
      }
    };
    // Jeder Anmeldewechsel beginnt eine neue Generation; ältere Ladeversuche werden verworfen
    let generation = 0;
    let backgroundRetry: ReturnType<typeof setTimeout> | null = null;
    let isLoadInFlight = false;
    let hadUser = false;
    const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

    // Firestore behält gelesene Dokumente im Browser (IndexedDB, ausser Safari). Ohne Löschen zeigte die App
    // der nächsten Person im selben Browser Projekte der vorherigen aus dem Cache, bis der Server ablehnt.
    // Löschen geht nur, solange Firestore noch nichts gelesen hat: beim Start ohne Anmeldung.
    const clearFirestoreCache = () => {
      try {
        clearIndexedDbPersistence(db).catch((e) => console.warn('[AuthContext] Firestore cache not cleared', e));
      } catch (e) {
        console.warn('[AuthContext] Firestore cache not cleared', e);
      }
    };

    // Firestore behält seine Verbindung mit dem App-Check-Token, mit dem sie aufgebaut wurde; Versuche über
    // dieselbe Verbindung scheitern sofort wieder. Neu verbinden, damit sie ein frisches Token holt.
    const reconnectFirestore = () => disableNetwork(db)
      .then(() => enableNetwork(db))
      .catch((e) => console.warn('[AuthContext] Firestore reconnect failed', e));

    /** Liest das eigene Profil; scheitert es, erneut nach den angegebenen Wartezeiten (jeweils neu verbunden). */
    const fetchProfileWithRetry = async (user: User, gen: number, retries: number[], reconnectFirst: boolean) => {
      const profileRef = doc(db, 'users', user.uid);
      for (let attempt = 0; ; attempt++) {
        try {
          if (attempt > 0 || reconnectFirst) await reconnectFirestore();
          return await getDoc(profileRef);
        } catch (error) {
          if (gen !== generation) return null;
          if (attempt >= retries.length) throw error;
          console.warn(`[AuthContext] Profil nicht geladen (Versuch ${attempt + 1}), neuer Versuch in ${retries[attempt] / 1000}s`, error);
          setIsReconnecting(true);
          await sleep(retries[attempt]);
          if (gen !== generation) return null;
        }
      }
    };

    /** Profil laden; scheitern alle Versuche, Verbindungsfehler melden und im Hintergrund weiter versuchen. */
    const loadProfile = async (user: User, gen: number, retries: number[]) => {
      if (backgroundRetry) {
        clearTimeout(backgroundRetry);
        backgroundRetry = null;
      }
      isLoadInFlight = true;
      try {
        // 2. FORCE INITIAL FETCH (Sequential)
        // This prevents the "hanging loading state" race condition by ensuring 
        // we have the state (or lack thereof) before relying on the socket listener.
        // Wiederholungen im Hintergrund und von Hand folgen auf einen Fehler: zuerst neu verbinden
        let initialSnap = await fetchProfileWithRetry(user, gen, retries, retries.length === 0);
        if (!initialSnap || gen !== generation) return;
        const profileRef = initialSnap.ref;

        // SSO Fallback: If SSO user has no profile, call provisionSsoUser to create one
        if (!initialSnap.exists()) {
          const isSsoUser = user.providerData?.some(p => p.providerId?.startsWith('saml.') || p.providerId?.startsWith('oidc.'));
          if (isSsoUser && firebaseFunctions) {
            try {
              console.log('[AuthContext] SSO user without profile — calling provisionSsoUser...');
              const provisionFn = httpsCallable(firebaseFunctions, 'provisionSsoUser');
              await provisionFn({});
              // Force token refresh to pick up new custom claims (tenantId, role, accessLevel)
              await user.getIdToken(true);
              // Re-read the profile after provisioning
              initialSnap = await getDoc(profileRef);
              console.log('[AuthContext] Profile after provisioning:', initialSnap.exists());
            } catch (provisionErr) {
              console.error('[AuthContext] provisionSsoUser failed:', provisionErr);
            }
          }
        }

        if (initialSnap.exists()) {
          const profile = initialSnap.data() as UserProfile;
          setUserProfile(profile);

          // Log LOGIN event once per session via backend
          if (!loginLogged.current && profile.tenantId && firebaseFunctions) {
            try {
              const logLoginFn = httpsCallable(firebaseFunctions, 'loglogin');
              // Fire-and-forget; wechselt die Seite gleich nach der Anmeldung, bricht der Aufruf ab
              logLoginFn({}).catch((e) => console.warn('[AuthContext] loglogin call failed (non-fatal)', e));
            } catch (e) {
              console.warn('[AuthContext] loglogin call failed (non-fatal)', e);
            }
            loginLogged.current = true;
          }
        } else {
          setUserProfile(null);
        }

        // 3. Unblock the UI *immediately* after initial fetch
        setProfileLoadFailed(false);
        setIsReconnecting(false);
        setIsRetryingProfile(false);
        setLoading(false);

        // 4. Then subscribe for real-time updates (Background)
        if (unsubscribeProfileSnapshot) unsubscribeProfileSnapshot();
        let lastClaimsUpdate = initialSnap.exists() ? (initialSnap.data() as UserProfile).claimsUpdatedAt : undefined;
        unsubscribeProfileSnapshot = onSnapshot(profileRef, (docSnap) => {
          if (docSnap.exists()) {
            const profile = docSnap.data() as UserProfile;
            // Rollen oder Einheit wurden geändert: ID-Token mit den neuen Claims holen
            if (profile.claimsUpdatedAt && profile.claimsUpdatedAt !== lastClaimsUpdate) {
              lastClaimsUpdate = profile.claimsUpdatedAt;
              user.getIdToken(true).catch((e) => console.warn('[AuthContext] Token refresh failed', e));
            }
            setUserProfile(profile);
          } else {
            setUserProfile(null);
          }
        }, (error) => {
          console.warn("Profile snapshot error:", error);
          // We don't reset loading here because we already handled the initial load
        });

      } catch (error) {
        if (gen !== generation) return;
        console.error("Auth initialization error:", error);
        // Nicht "kein Profil": Die App zeigt einen Verbindungsfehler und versucht es weiter
        setUserProfile(null);
        setProfileLoadFailed(true);
        setIsReconnecting(false);
        setIsRetryingProfile(false);
        setLoading(false); // Ensure we don't hang even on error
        backgroundRetry = setTimeout(() => loadProfile(user, gen, []), PROFILE_BACKGROUND_RETRY_MS);
      } finally {
        if (gen === generation) isLoadInFlight = false;
      }
    };

    const unsubscribeAuth = auth.onAuthStateChanged(async (user) => {
      // Sitzung beendet (hier, in einem anderen Tab oder vom Server): neu starten, der Start leert den Cache.
      // Auch andere Tabs laden neu, sonst hielten sie die Datenbank offen und blockierten das Löschen.
      if (!user && hadUser) {
        window.location.reload();
        return;
      }
      hadUser = !!user;
      // 1. Clean up previous listener if user changes or initially
      if (unsubscribeProfileSnapshot) {
        unsubscribeProfileSnapshot();
        unsubscribeProfileSnapshot = null;
      }
      const gen = ++generation;
      isLoadInFlight = false;
      if (backgroundRetry) {
        clearTimeout(backgroundRetry);
        backgroundRetry = null;
      }
      setProfileLoadFailed(false);
      setIsReconnecting(false);
      setIsRetryingProfile(false);

      setCurrentUser(user);
      if (!user) setIsSigningOut(false);

      if (user) {
        // Reset loading state — prevents flash of "no tenant" screen
        // while profile is being fetched/provisioned
        setLoading(true);
        // Von Hand oder beim Wechsel auf online (nicht im Hintergrund, sonst Endlosschleife bei blockiertem Skript)
        retryProfileRef.current = () => {
          if (gen !== generation || isLoadInFlight) return;
          if (!USE_EMULATORS && !turnstileAppCheckProviderInstance.isScriptLoaded()) {
            window.location.reload();
            return;
          }
          setIsRetryingProfile(true);
          void loadProfile(user, gen, []);
        };
        await loadProfile(user, gen, PROFILE_RETRY_DELAYS_MS);
      } else {
        // No user
        clearFirestoreCache();
        retryProfileRef.current = () => {};
        setUserProfile(null);
        setLoading(false);
      }
    });

    // Wieder online: sofort neu versuchen statt auf die nächste Wiederholung zu warten
    const handleOnline = () => {
      if (backgroundRetry) retryProfileRef.current();
    };
    window.addEventListener('online', handleOnline);

    return () => {
      generation++;
      unsubscribeAuth();
      window.removeEventListener('online', handleOnline);
      if (backgroundRetry) clearTimeout(backgroundRetry);
      if (unsubscribeProfileSnapshot) {
        unsubscribeProfileSnapshot();
      }
    };
  }, []);

  const retryProfileLoad = React.useCallback(() => retryProfileRef.current(), []);

  // Zuerst baut React die angemeldete Oberfläche ab (isSigningOut), erst danach wird abgemeldet (Effekt unten).
  // Sonst fragte Firestore die noch offenen Listener mit der neuen, leeren Anmeldung erneut an, und die
  // Regeln lehnten sie ab.
  const logout = React.useCallback(() => {
    if (!signOutRef.current) {
      let finish = () => {};
      const done = new Promise<void>(resolve => { finish = resolve; });
      signOutRef.current = { done, finish };
      setIsSigningOut(true);
    }
    return signOutRef.current.done;
  }, []);

  // Läuft nach dem Aufräumen der Ansichten: React baut im selben Commit zuerst ab, dann laufen neue Effekte.
  // Nach signOut lädt onAuthStateChanged die Seite neu (Cache leeren); Firestore bleibt bis dahin offline.
  useEffect(() => {
    if (!isSigningOut) return;
    void (async () => {
      try {
        stopProfileListenerRef.current();
        // Ohne Netz enden laufende Einzelabfragen aus dem Cache, statt nach dem Abmelden abgelehnt zu werden
        await disableNetwork(db);
        await signOut(auth);
      } catch (error) {
        console.error('[AuthContext] Logout failed', error);
        await enableNetwork(db).catch((e) => console.warn('[AuthContext] Firestore reconnect failed', e));
        setIsSigningOut(false);
      } finally {
        signOutRef.current?.finish();
        signOutRef.current = null;
      }
    })();
  }, [isSigningOut]);

  const permissions = React.useMemo(() => derivePermissions(userProfile), [userProfile]);

  const value = {
    currentUser,
    userProfile,
    loading,
    isAdmin: permissions.isTenantAdmin,
    isSuperAdmin: permissions.isSuperAdmin,
    isAssessor: permissions.isAssessor,
    permissions,
    profileLoadFailed,
    isRetryingProfile,
    retryProfileLoad,
    logout,
  };

  return (
    <AuthContext.Provider value={value}>
      {loading
        ? (isReconnecting && <ConnectionStatusScreen variant="connecting" />)
        : !isSigningOut && children}
    </AuthContext.Provider>
  );
};